// The SUPPORT panel of the hub (R229), in the section shell
// (ui-map-interface.md §16.3, §16.16, R1916).
//
// What it shows, in five sections in the rail order: QUESTIONS, the 42
// questions a new commander asks in eight groups, with the answer the code
// and the design docs actually give (R2561); GLOSSARY, the player glossary under its filter;
// KEYS, the key sheet generated from `ui/keymap`; TOUCH, the gestures the
// map understands; and ASK A PERSON, where to ask a person. ASK A PERSON
// is also the companion column of every other section, with the fold
// `rail`: its rail row stands only while the column cannot, and its tile
// always stands. A wide pane opens on QUESTIONS.
//
// A summary states the figure of the section's own content and the
// reader's open state or filter (2 OF 42 OPEN, 7 OF 84 WORDS), because
// the panel reads no live data. ASK A PERSON states ADDRESS SHOWN after
// the press, and nothing before it.
//
// What it reads live: nothing. Every word here is checked against the
// repository and none of it is a route. `KEYMAP` is documentation-as-data
// and the vitest guards it, so the key sheet cannot drift from the
// handlers. The gestures come from `ui/touch.ts` and `render/camera.ts`.
//
// What is honestly absent today:
//   - **There is no ticket system.** The mockup drew a ticket list with
//     three rows. Nothing stores a ticket, so the card says a person of
//     the team answers on Discord or by e-mail, and draws no row. The address itself
//     is behind a press (R604): the two halves of it reach this module as
//     two constants of `gen/balance`, and the code joins them with the
//     "@" only while it renders the anchor. A harvester that reads the
//     bundle or the static HTML therefore finds no address, and one that
//     runs the page still has to press the button. The panel holds no
//     joined address anywhere else. The column and the ASK A PERSON page
//     draw one contact body, and the pane holds one of the two at a time.
//     The `revealed` flag lives with the panel and `close` clears it, so
//     the address goes out of the document again when the section is left.
//   - **There is no step list.** The mockup drew a card of seven steps
//     with progress marks. Nothing implements one, so the panel draws no
//     such card. The game gives a new player the advisor line instead: one
//     next step above the build queue, on by default for the first Core
//     seasons (player-reach.md §4). The first question points at it
//     (R1555).
//   - **Every answer below carries a figure only where the figure is
//     decided.** The arcade caps are configuration (R224), so the answer
//     names the default and points at the counter for the true figure.

import { button, linkButton } from '../../choice/choice';
import { shownKeymap } from '../../keymap';
import { openLink } from '../../../platform/links';
import {
  ADVISOR_DEFAULT_ON_SEASONS, ARCADE_RUNS_FREE, ARCADE_RUNS_GUEST, ATTR_POINT_GAIN, ATTR_TRAIN_CAP, CAPTAIN_ATTRS,
  CAPTAIN_SLOTS_BASE, CAPTAIN_SLOTS_MAX, DECLINE_AT_AGE, DISCORD_URL, MAX_CLAN_MEMBERS,
  MAX_QUEUE_HOURS, MAX_QUEUE_HOURS_EXTENDED, NAME_MAX_LEN, NAME_MIN_LEN, PEAK_AT_AGE,
  PLAYER_REPORT_TEXT_MAX_RUNES, RECRUIT_AGE_MAX, RECRUIT_AGE_MIN, REPORT_NOTE_RUNES,
  SEASON_DAYS, SHARED_INTEL_LEVEL, YEARS_PER_SEASON,
  SQUAD_COUNT, SQUAD_SIZE, SUPPORT_MAIL_DOMAIN, SUPPORT_MAIL_USER,
} from '../../../gen/balance';
import { PAGE_PREFIX, hubSupport as S } from '../../../gen/strings';
import { closeHowPlays, HOW_MODES, howModeOf, modeName, openHowPlays } from '../../howplays';
import { NETTEST_HREF } from '../../reportsheet';
import {
  ctlRow, esc, h1, lede, plain, rows, section, wireOnce,
  type Panel, type PanelContext, type Row,
} from '../panel';
import {
  SectionNav, cardGrid, shellHtml, shellPress, type Companion, type ShellPage, type ShellSection,
} from '../sectionshell';
import { TERMS, filterTerms } from '../../glossary';

/** The five sections of SUPPORT, in the rail order (§16.16). The ids are
 *  the address words: `?tab=support&section=glossary`. */
export const SUPPORT_SECTIONS = ['questions', 'glossary', 'keys', 'touch', 'ask'] as const;
type SupportSection = (typeof SUPPORT_SECTIONS)[number];

/** The natural words of a section, and the old words of its cards. */
const SUPPORT_ALIASES: Readonly<Record<string, SupportSection>> = {
  faq: 'questions', keymap: 'keys', gestures: 'touch', contact: 'ask', mail: 'ask',
};

/** The press of a rail row and of a tile, and ‹ SUPPORT of a narrow page. */
const GO = 'support-sec';
const BACK = 'support-back';
const SHELL = { go: GO, back: BACK };

/** One question and the answer the code gives. */
interface QA {
  q: string;
  /** The paragraphs of the answer. */
  a: string[];
}

/** One group of questions under a head (R1744). */
interface QAGroup {
  head: string;
  list: QA[];
}

/**
 * The questions, in eight groups (R1744, R2561): the first steps, the
 * empire, the fleets and the captains, combat and sonar, the territory and
 * the sieges, the seasons and the clans, the device and the connection,
 * and the account and the money.
 *
 * Every answer was read out of the repository before it was written here.
 * The comment on each question names the page it was read on; the player
 * sees no source line (R1732). Where a figure is configuration rather than a
 * rule, the answer says so instead of printing a number that an operator
 * may have moved. The words themselves live in the locale lane (R343);
 * this table is the order they are asked in.
 */
const GROUPS: QAGroup[] = [
  {
    head: S.group_start,
    list: [
      {
        // The advisor line (R1555). The season count is the pack's, so the
        // answer cannot drift from the rule the client's `advisorOn` reads.
        q: S.q_next_step,
        a: [S.a_next_step_1, S.a_next_step_2({ seasons: ADVISOR_DEFAULT_ON_SEASONS })],
        // Read on: player-reach.md §4, §8 · R1566 · ui/advisor.ts · ui/trials.ts.
      },
      {
        // How a conquest season is won (R2445): the words of the HOW IT
        // PLAYS card. The figures are the pack's.
        q: S.q_win_core,
        a: [
          S.a_win_core_1({ days: SEASON_DAYS }),
          S.a_win_core_2({ squad: SQUAD_SIZE, members: MAX_CLAN_MEMBERS }),
        ],
        // Read on: player-reach.md §8.7 · game/season.go (ScoreBreakdown,
        // HonourFor) · hub/season.go seasonHonour · hub/objects.go
        // creditSiege · hub/fanout.go · numbers.md §8, §10.
      },
      {
        // How an arcade match is won (R2445).
        q: S.q_win_arcade,
        a: [S.a_win_arcade_1, S.a_win_arcade_2],
        // Read on: arcade.md §4.5, §6.1, §18 · R294, R1196.
      },
      {
        q: S.q_join,
        a: [S.a_join_1, S.a_join_2],
        // Read on: deployment.md §7 (R404, R768) · CONTEXT.md "held world",
        // "walk-in joiner" · R240 · autopilot.md §1.2 (R736) · numbers.md §4
        // founding truce (R51).
      },
      {
        q: S.q_build_first,
        a: [S.a_build_first_1, S.a_build_first_2],
        // Read on: hub/autopilot_season.go baseLadder · autopilot.md §2.2 ·
        // player-reach.md §4.2, §4.4, §8 · trials.toml · hub.toml
        // starting_squadron · numbers.md §2, §9.
      },
      {
        // The ceiling every seat holds at least; Premium holds more.
        q: S.q_offline,
        a: [S.a_offline_1, S.a_offline_2({ hours: MAX_QUEUE_HOURS })],
        // Read on: autopilot.md §1.2 · hub/autopilot_season.go
        // releaseToHuman · hub/standing.go (R1256) · numbers.md §4 (R86,
        // R1154, R1444).
      },
      {
        // The drill (arcade.md §17, R1573): the fight a new player learns in,
        // with no run spent and nothing at risk.
        q: S.q_drill,
        a: [S.a_drill_1, S.a_drill_2],
        // Read on: arcade.md §6, §17 · R1564, R1573, R1581, R1931 ·
        // player-reach.md §8.
      },
    ],
  },
  {
    head: S.group_empire,
    list: [
      {
        q: S.q_resources,
        a: [S.a_resources_1, S.a_resources_2],
        // Read on: numbers.md §2, §9, §17 · glossary.toml · game/economy.go ·
        // game/expeditions.go surface (R62, R233, R643).
      },
      {
        q: S.q_credits,
        a: [S.a_credits_1, S.a_credits_2],
        // Read on: numbers.md §1 (R121, R1073), §12, §17, §18 · casino.md ·
        // game/queue.go runQueue, RushCost.
      },
      {
        q: S.q_queue_ceiling,
        a: [
          S.a_queue_ceiling_1({
            hours: MAX_QUEUE_HOURS, extended: MAX_QUEUE_HOURS_EXTENDED,
          }),
          S.a_queue_ceiling_2,
        ],
        // Read on: numbers.md §1 (R33, R212, R726) · monetization.md §4, §5 ·
        // perks.toml everyone (R211) · buildings.toml build_slots_second_at ·
        // tech-tree I8 (R711).
      },
      {
        q: S.q_queue_stuck,
        a: [S.a_queue_stuck_1, S.a_queue_stuck_2],
        // Read on: game/queue.go Enqueue, runQueue, Cancel ·
        // game/buildings.go buildingOrder · notices.toml · numbers.md §1
        // (R387, R417, R1109).
      },
      {
        q: S.q_research,
        a: [S.a_research_1, S.a_research_2],
        // Read on: tech-tree.md §1, §1.1, §1.2, §2 · game/tech.go
        // researchOrder · game/expeditions.go UseDataCore.
      },
      {
        q: S.q_better_design,
        a: [S.a_better_design_1, S.a_better_design_2],
        // Read on: numbers.md §6 (R1194, R1884), §17 (R217) ·
        // game/blueprints.go · game/patterns.go.
      },
      {
        q: S.q_expedition,
        a: [S.a_expedition_1, S.a_expedition_2],
        // Read on: numbers.md §11 (R673, R738, R1086, R1793) ·
        // game/expeditions.go StartExpedition.
      },
    ],
  },
  {
    head: S.group_fleets,
    list: [
      {
        q: S.q_put_to_sea,
        a: [S.a_put_to_sea_1, S.a_put_to_sea_2],
        // Read on: numbers.md §2, §3 (R1880), §16 "Who may sail", §17 ·
        // hub/commands.go createFleet, deployHull · game/captains.go
        // AvailableForCommand · ui/fleetlist.ts dockedRow.
      },
      {
        q: S.q_give_order,
        a: [S.a_give_order_1, S.a_give_order_2],
        // Read on: ui-map-interface.md §8 "The RTS grammar (v5)", "The touch
        // grammar" · CONTEXT.md "drag order" (R1208), "order row" ·
        // numbers.md §4, §16 (R2274) · R1307.
      },
      {
        q: S.q_repair,
        a: [S.a_repair_1, S.a_repair_2],
        // Read on: numbers.md §1 "Repair priority" (R33), §3 Repair (R1880),
        // §4 Shields (R8, R1276), §16 Return · hub/commands.go enqueueRepair.
      },
      {
        q: S.q_fleet_sunk,
        a: [S.a_fleet_sunk_1, S.a_fleet_sunk_2],
        // Read on: hub/losses.go mournLoss, captainFate, captorAt ·
        // game/captains.go Wound, Capture, FreeCaptain · numbers.md §12
        // "Sinking", §18.
      },
      {
        // A point of Gunnery or Helmsmanship is ATTR_POINT_GAIN; the
        // other attributes have rates of their own (numbers.md §12).
        q: S.q_captain_life,
        a: [
          S.a_captain_life_1({
            attrs: CAPTAIN_ATTRS, cap: ATTR_TRAIN_CAP, per_point: ATTR_POINT_GAIN,
            peaked: ATTR_TRAIN_CAP * ATTR_POINT_GAIN,
          }),
          S.a_captain_life_2({
            min: RECRUIT_AGE_MIN, max: RECRUIT_AGE_MAX, years: YEARS_PER_SEASON,
            peak: PEAK_AT_AGE, decline: DECLINE_AT_AGE,
            slots: CAPTAIN_SLOTS_BASE, slots_max: CAPTAIN_SLOTS_MAX,
          }),
        ],
        // Read on: numbers.md §12 (R2563) · game/season.go AgeCaptains ·
        // hub/season.go closeSeason · monetization.md §4 Cryo Freeze.
      },
    ],
  },
  {
    head: S.group_combat,
    list: [
      {
        q: S.q_fight,
        a: [S.a_fight_1, S.a_fight_2],
        // Read on: numbers.md §4 (R407, R678, R1159), §4.1 (R2375, R2387,
        // R2389) · CONTEXT.md "approach", "aspect", "gun arc", "signature
        // order".
      },
      {
        q: S.q_torpedoes,
        a: [S.a_torpedoes_1, S.a_torpedoes_2],
        // Read on: numbers.md §3, §5 (R657, R1788, R2017) ·
        // ui-map-interface.md §8 "Aiming" · CONTEXT.md "load mix", "active
        // round", "LOADOUT sheet", "round pick".
      },
      {
        q: S.q_fog_of_war,
        a: [S.a_fog_of_war_1, S.a_fog_of_war_2],
        // Read on: numbers.md §7 "Who hears what" (R112, R412) · server
        // fan-out.
      },
      {
        q: S.q_stay_unheard,
        a: [S.a_stay_unheard_1, S.a_stay_unheard_2],
        // Read on: numbers.md §3, §4 (R1437), §4.1 (R2374), §5, §6, §7, §14 ·
        // sim/fleet.go NoiseFactor.
      },
    ],
  },
  {
    head: S.group_territory,
    list: [
      {
        q: S.q_territory,
        a: [S.a_territory_1, S.a_territory_2],
        // Read on: numbers.md §8 Tier 1, "How territory moves: the beam" ·
        // sim/missions.go StartAnchor, AnchorableAt (R116) · ships.toml
        // Beacon.
      },
      {
        // A player never sieges, takes or captures a hostile hold.
        q: S.q_enemy_hold,
        a: [S.a_enemy_hold_1, S.a_enemy_hold_2],
        // Read on: CONTEXT.md "hold", "siege", "core loop" · numbers.md §4,
        // §8 · hub/losses.go.
      },
      {
        q: S.q_siege,
        a: [S.a_siege_1, S.a_siege_2],
        // Read on: numbers.md §8 Tier 2, "Objective bonuses" · sim/objects.go
        // (R1456) · sim.toml [sim.objects].
      },
      {
        q: S.q_supply,
        a: [S.a_supply_1, S.a_supply_2],
        // Read on: numbers.md §8 Tier 1 (R126, R183, R703) · CONTEXT.md
        // "cut-off ground", "rim".
      },
      {
        q: S.q_two_clocks,
        a: [S.a_two_clocks_1, S.a_two_clocks_2],
        // Read on: numbers.md §15, §19, §19.1, "Gathering is map time" · R91,
        // R2091 · CONTEXT.md "world clock", "map clock", "combat pace".
      },
    ],
  },
  {
    head: S.group_seasons,
    list: [
      {
        q: S.q_season_reset,
        a: [S.a_season_reset_1, S.a_season_reset_2],
        // Read on: game-design §3 "Persists" · game/season.go · numbers.md §18.
      },
      {
        q: S.q_season_end,
        a: [S.a_season_end_1({ days: SEASON_DAYS }), S.a_season_end_2],
        // Read on: numbers.md §15 (R2090, R2091, R2092) · CONTEXT.md "floor
        // shift", "war economy", "settlement", "drain".
      },
      {
        q: S.q_honour,
        a: [S.a_honour_1, S.a_honour_2],
        // Read on: game-design.md §9 "Honour" (R23) · CONTEXT.md "Honour",
        // "Clan Honour" · data/identity/labels.toml rank_titles.
      },
      {
        q: S.q_clan_squad,
        // The squad figure is the generated one, so the answer cannot drift
        // from the plan the meta server holds.
        a: [
          S.a_clan_squad_1({ members: MAX_CLAN_MEMBERS, squads: SQUAD_COUNT }),
          ...(SQUAD_COUNT > 1 ? [S.a_clan_squad_2] : []),
          S.a_clan_free,
        ],
        // Read on: R222, R228 · numbers.md §10 · deployment.md §7.
      },
      {
        q: S.q_squad,
        a: [S.a_squad_1, S.a_squad_2({ intel: SHARED_INTEL_LEVEL })],
        // Read on: player-reach.md §2, §5.2 · numbers.md §10 · hub/social.go ·
        // hub/war.go intelFor · hub/sonar.go.
      },
    ],
  },
  {
    head: S.group_device,
    list: [
      {
        q: S.q_phone_play,
        a: [S.a_phone_play_1, S.a_phone_play_2],
        // Read on: ui-map-interface.md §12 · the GESTURES table below ·
        // ui.toml gest_*, help_gestures (R1935, R1936) · hub_alerts
        // ios_step · player-reach.md §3.5.
      },
      {
        q: S.q_screen_size,
        a: [S.a_screen_size_1, S.a_screen_size_2],
        // Read on: ui-map-interface.md §13 "The UI scale" (R1556) ·
        // ui/options/rows.ts ifaceRows, socialRows, mapRows.
      },
      {
        // The CHECK THE NETWORK press of ASK A PERSON (R2561) reaches the
        // check when the game page never loads.
        q: S.q_connection,
        a: [S.a_connection_1, S.a_connection_2],
        // Read on: R108 · ui-map-interface.md §8 "The link timer" · ui.toml
        // link_lost, link_not_retrying, fatal_protocol_*, [ui.maintenance] ·
        // fault-reports.md §9 (R1707) · ui/options/gamemenu.ts helpRows.
      },
      {
        q: S.q_report_problem,
        a: [S.a_report_problem_1({ max: PLAYER_REPORT_TEXT_MAX_RUNES }), S.a_report_problem_2],
        // Read on: fault-reports.md §4.1, §4.4, §4.5 (R1583, R1622, R1626,
        // R1628) · [ui.report_sheet] · arcade/main.ts reportSheet (R1936).
      },
    ],
  },
  {
    head: S.group_money,
    list: [
      {
        q: S.q_rename,
        a: [S.a_rename_1, S.a_rename_2({ name_min: NAME_MIN_LEN, name_max: NAME_MAX_LEN })],
        // Read on: R226 · R2557.
      },
      {
        q: S.q_account_device,
        a: [S.a_account_device_1, S.a_account_device_2],
        // Read on: deployment.md §10.3 (R607, R1565) · [ui.hub_settings]
        // account_lede_guest, signin_* · [ui.guest_warn].
      },
      {
        q: S.q_block_report,
        a: [S.a_block_report_1, S.a_block_report_2({ note: REPORT_NOTE_RUNES })],
        // Read on: deployment.md §7.6 (R1312, R1323, R1328) · [ui.hub_report],
        // [ui.hub_blocks].
      },
      {
        q: S.q_arcade_runs,
        a: [S.a_arcade_runs_1({ free: ARCADE_RUNS_FREE, guest: ARCADE_RUNS_GUEST }), S.a_arcade_runs_2],
        // Read on: R221, R224, R264 · arcade.md §2.3, §17 · monetization.md
        // §2, §4 · panels/play.ts arcadeState.
      },
      {
        q: S.q_never_sell,
        a: [S.a_never_sell_1, S.a_never_sell_2],
        // Read on: monetization.md · the never-sell list.
      },
    ],
  },
];

/** The questions in the order of the groups. The index of a question
 *  here is its `data-i`. */
const QUESTIONS: QA[] = GROUPS.flatMap((g) => g.list);

/**
 * The gestures the map understands with a finger.
 *
 * A finger has one button and no hover, and iOS raises no `contextmenu`
 * over a canvas, so the tap grammar carries every command the mouse has.
 */
const GESTURES: Row[] = [
  [S.gesture_drag, S.gesture_drag_does],
  [S.gesture_pinch, S.gesture_pinch_does],
  [S.gesture_tap_empty, S.gesture_tap_empty_does],
  [S.gesture_tap_water, S.gesture_tap_water_does],
  [S.gesture_tap_order, S.gesture_tap_order_does],
  [S.gesture_tap_other_fleet, S.gesture_tap_other_fleet_does],
  [S.gesture_long_press, S.gesture_long_press_does],
  [S.gesture_tap_minimap, S.gesture_tap_minimap_does],
  [S.gesture_aim_first, S.gesture_aim_first_does],
  [S.gesture_aim_second, S.gesture_aim_second_does],
];

/** The bindings the key sheet shows: every key that is not hidden (R456),
 *  less the casino keys in a store app (R2927). */
function keyCount(): number {
  return shownKeymap().reduce((n, g) => n + g.keys.filter((k) => !k.hidden).length, 0);
}

export function createSupportPanel(ctx: PanelContext): Panel {
  /** Which answers are open. A person may hold two questions at once. */
  const open = new Set<number>();

  /** True once a person has pressed for the support address (R604). The
   *  address is in no document until this is true. */
  let revealed = false;

  /** What the glossary filter holds (R1557). */
  let query = '';

  /** The open section and the section the address named (§16.16). */
  const nav = new SectionNav({ ids: () => SUPPORT_SECTIONS, first: () => 'questions', aliases: SUPPORT_ALIASES });

  /**
   * The support address, made here and nowhere else.
   *
   * The two halves travel apart on the compile-time lane, and the "@"
   * goes between them at render time, so the whole address exists only in
   * a document that a press built.
   */
  function supportMail(): string {
    return [SUPPORT_MAIL_USER, SUPPORT_MAIL_DOMAIN].join('@');
  }

  /** The contact body: the lede and the four buttons. The address is a
   *  button until it is pressed, and a link button to it after (R1348,
   *  R2011). CHECK THE NETWORK links the network check page (R1707,
   *  R2561), so a player whose game page never loads reaches the check
   *  from HELP; it stands last, after the address.
   *  The companion column draws this body, and the ASK A PERSON page draws
   *  it in its card; the pane holds one of the two at a time. */
  function askBody(): string {
    const mail = revealed
      ? linkButton(supportMail(), `mailto:${supportMail()}`, { variant: 'primary' })
      : button(S.pill_show_mail, { act: 'mail' });
    return lede(S.ask_lede)
      + ctlRow(
        button(S.pill_discord, { act: 'discord' }, { variant: 'primary' })
        + button(S.pill_contact, { act: 'contact' })
        + mail
        + linkButton(S.pill_nettest, NETTEST_HREF),
      );
  }

  /** The body of the ASK A PERSON page: the contact body alone, with no
   *  box and no second title (R1945). */
  function askCard(): string {
    return plain(askBody());
  }

  /**
   * The question list: a disclosure list under eight heads (R1744,
   * R2561). A
   * question is a sentence, so it takes the Body type in mixed case and
   * not a pill. Its row is one button with a chevron that turns when the
   * answer opens; `aria-expanded` and `aria-controls` tell a screen reader
   * the same. One press opens or closes one answer, and a reader may hold
   * two answers open at once. A closed answer stays in the markup,
   * `hidden`, so the button always names a region that exists. The title
   * line of the page states the count. The page has one section, so the
   * list stands with no box and no head of its own (R1945).
   */
  function qaCard(): string {
    let i = 0;
    const groups = GROUPS.map((g, gi) => {
      const rowsHtml = g.list.map((it) => {
        const n = i++;
        const on = open.has(n);
        return `<div class="qa${on ? ' on' : ''}">`
          + `<button type="button" class="qa-q" id="qa-q-${n}" data-act="qa" data-i="${n}"`
          + ` aria-expanded="${on}" aria-controls="qa-a-${n}">`
          + `<span class="qa-t">${esc(it.q)}</span><span class="qa-c" aria-hidden="true"></span></button>`
          + `<div class="qa-a" id="qa-a-${n}" role="region" aria-labelledby="qa-q-${n}"${on ? '' : ' hidden'}>`
          + it.a.map((p) => lede(p)).join('') + '</div></div>';
      }).join('');
      return `<h3 class="qa-h" id="qa-h-${gi}">${esc(g.head)}</h3>`
        + `<div class="qa-g" role="group" aria-labelledby="qa-h-${gi}">${rowsHtml}</div>`;
    }).join('');
    return plain(howRow() + groups);
  }

  /** The HOW IT PLAYS row at the top of the question list (R2445): one
   *  lede and a press for each page of the card. */
  function howRow(): string {
    return `<div class="qa-how">${lede(S.how_lede)}`
      + ctlRow(HOW_MODES.map((m) => button(modeName(m), { act: 'how', data: { mode: m } })).join(''))
      + '</div>';
  }

  /** The terms the filter keeps. */
  function kept(): Set<string> {
    return new Set(filterTerms(TERMS, query).map((t) => t.id));
  }

  /** The GLOSSARY summary: the term count, or the terms the filter keeps
   *  while it holds a word. */
  function glossarySum(keep: number): string {
    return query.trim() === ''
      ? S.card_glossary_count({ count: TERMS.length })
      : S.sum_glossary_filter({ shown: keep, count: TERMS.length });
  }

  /**
   * The GLOSSARY card (R1557): the player glossary, each word over its
   * definition, under a field that filters it. A row the filter drops is
   * `hidden` and stays in the markup, so a keystroke rewrites no pane and
   * the field keeps its focus; `wire` hides and shows the rows in place.
   * The page has one section, so it draws no box and no head (R1945).
   */
  function glossaryCard(): string {
    const keep = kept();
    const list = TERMS.map((t) => `<div class="gterm" data-term="${esc(t.id)}"`
      + `${keep.has(t.id) ? '' : ' hidden'}><div class="gw">${esc(t.word)}</div>`
      + `${lede(t.def)}</div>`).join('');
    // The terms flow in columns of the width of one term (R1751): two or
    // three on a wide pane, one on a telephone. A term never breaks across
    // two columns.
    return plain(
      lede(S.glossary_lede)
      + `<input type="text" class="gfilter" data-in="glossary" value="${esc(query)}"`
      + ` placeholder="${esc(S.glossary_filter)}" aria-label="${esc(S.glossary_filter)}">`
      + `<div class="gterms">${list}</div>`
      + `<div class="lede gnone"${keep.size > 0 ? ' hidden' : ''}>${esc(S.glossary_none)}</div>`);
  }

  /** One section per key group, under the sub-header of the group and
   *  its key count (R1945). The rows are the bindings themselves, so the
   *  sheet cannot drift from the handlers. A hidden binding is documented in
   *  ui-map-interface.md §9 and not here, and a group whose rows are every
   *  one hidden drops its section too — the same rule the in-game KEYS panel
   *  follows (R456). */
  function keyCards(): string[] {
    return shownKeymap().map((g) => {
      const keys = g.keys.filter((k) => !k.hidden);
      return keys.length === 0 ? ''
        : section(g.name, S.sum_keys({ count: keys.length }),
          `<div class="keyrows">${rows(keys.map((k): Row => [k.key, k.does]))}</div>`);
    });
  }

  /** The five sections, each with its summary (§16.16, R1916). */
  function sections(): ShellSection[] {
    return [
      {
        id: 'questions', glyph: '?', name: S.sec_questions,
        sum: open.size > 0
          ? S.sum_questions_open({ open: open.size, count: QUESTIONS.length })
          : S.card_qa_count({ count: QUESTIONS.length }),
      },
      { id: 'glossary', glyph: '≡', name: S.card_glossary_title, sum: glossarySum(kept().size) },
      { id: 'keys', glyph: '⌨', name: S.card_keys_title, sum: S.sum_keys({ count: keyCount() }) },
      { id: 'touch', glyph: '◉', name: S.sec_touch, sum: S.sum_touch({ count: GESTURES.length }) },
      {
        id: 'ask', glyph: '✉', name: S.sec_ask, sum: revealed ? S.sum_ask_shown : '',
        wideTile: true, compact: true,
      },
    ];
  }

  /** The page of one section. ASK A PERSON is the companion of every
   *  other page; its own page draws the contact body and no column, so
   *  the pane holds one contact body, as CLAN holds one chat box. */
  function pageOf(id: SupportSection): ShellPage {
    const ask: Companion = { label: S.sec_ask, html: askBody(), fold: 'rail' };
    switch (id) {
      case 'ask':
        return { body: askCard() };
      case 'glossary':
        return { body: glossaryCard(), companion: ask };
      case 'keys':
        return { body: lede(S.keys_lede) + cardGrid(...keyCards()), companion: ask };
      case 'touch':
        return {
          body: plain(rows(GESTURES) + lede(S.gestures_lede)),
          companion: ask,
        };
      default:
        return { body: qaCard(), companion: ask };
    }
  }

  return {
    key: 'support',

    open(): void {
      nav.open();
    },

    /** The section was left. The address goes out of the document again. */
    close(): void {
      revealed = false;
      nav.close();
      closeHowPlays();
    },

    /** The address named a section (`?tab=support&section=ask`, §16.16). */
    openSection(word: string): void {
      nav.ask(word);
    },

    html(): string {
      const wide = ctx.wide();
      const at = nav.shown(wide);
      return shellHtml({
        title: S.title,
        heading: h1(S.title, S.subtitle),
        go: GO,
        back: BACK,
        sections: sections(),
        open: at,
        wide,
        page: pageOf(at ?? 'questions'),
      });
    },

    /** The glossary filter: each keystroke hides and shows the rows in
     *  place (R1557), and writes the GLOSSARY summary of the rail row and
     *  of the title line in place too, so no pane is rewritten under the
     *  field. Every button acts through the shell's press router. */
    wire(root: HTMLElement): void {
      nav.scroll(root);
      const box = root.querySelector<HTMLInputElement>('input[data-in="glossary"]');
      if (!box || !wireOnce(box)) return;
      box.addEventListener('input', () => {
        query = box.value;
        const keep = kept();
        root.querySelectorAll<HTMLElement>('.gterm[data-term]').forEach((row) => {
          row.hidden = !keep.has(row.dataset.term ?? '');
        });
        const none = root.querySelector<HTMLElement>('.gnone');
        if (none) none.hidden = keep.size > 0;
        const sum = glossarySum(keep.size);
        root.querySelectorAll<HTMLElement>('.o-it[data-id="glossary"] i, .hs-ptitle .o-pw').forEach((el) => {
          el.textContent = sum;
        });
      });
    },

    act(action: string, el: HTMLElement): void {
      if (shellPress(nav, ctx, SHELL, action, el)) return;
      switch (action) {
        case 'qa': {
          const i = Number.parseInt(el.dataset.i ?? '', 10);
          if (!Number.isFinite(i)) return;
          if (open.has(i)) open.delete(i);
          else open.add(i);
          ctx.play('sfx-menu-press');
          ctx.refresh();
          return;
        }
        // The HOW IT PLAYS row opens the card on one page (R2445).
        case 'how':
          ctx.play('sfx-menu-press');
          openHowPlays(howModeOf(el.dataset.mode), { press: () => ctx.play('sfx-menu-press') });
          return;
        case 'mail':
          revealed = true;
          ctx.play('sfx-menu-press');
          ctx.refresh();
          return;
        case 'discord':
          openLink(DISCORD_URL);
          return;
        case 'contact':
          // A link to a static document takes the prefix of the picked
          // language, as the two footers do (R856, R1331).
          openLink(`${PAGE_PREFIX}/contact`);
          return;
        default:
      }
    },
  };
}
